<template>
  <div class="bg-white shadow-sm border-b border-gray-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Платежные периоды</h1>
          <p class="mt-1 text-sm text-gray-500">
            Аналитика платежей по периодам
          </p>
        </div>
      </div>
    </div>
  </div>

  <div class="container mx-auto px-4 py-8">
    <div class="bg-white shadow-md rounded-lg overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <button
              @click="addAct"
              class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 hover:border-green-700 duration-150"
            >
              <i class="pi pi-plus"></i>
              <span>Добавить акт</span>
            </button>

            <button
              @click="addInvoice"
              class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 hover:border-blue-700 duration-150"
            >
              <i class="pi pi-plus"></i>
              <span>Добавить счет</span>
            </button>

            <button
              v-if="selectedItems.size > 0"
              @click="copyInvoices"
              :disabled="copyLoading"
              class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-orange-600 border border-orange-600 rounded-md hover:bg-orange-700 hover:border-orange-700 duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i v-if="!copyLoading" class="pi pi-copy"></i>
              <i v-else class="pi pi-spin pi-spinner"></i>
              <span>{{
                copyLoading ? 'Копирование...' : 'Копировать счета'
              }}</span>
            </button>
          </div>
        </div>
      </div>

      <ActForm
        :show="showAddActModal"
        mode="create"
        :act="actForm"
        :disableContractField="false"
        @close="showAddActModal = false"
        @submit="handleActSubmit"
      />

      <InvoiceForm
        :show="showAddInvoiceModal"
        mode="create"
        :invoice="invoiceForm"
        :disableContractField="false"
        @close="showAddInvoiceModal = false"
        @submit="handleInvoiceSubmit"
      />

      <div class="px-4 py-4 border-b border-gray-200 bg-gray-50">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <button
              @click="showFilters = !showFilters"
              class="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors duration-150"
            >
              <i
                class="pi transition-transform duration-200"
                :class="showFilters ? 'pi-chevron-up' : 'pi-chevron-down'"
              ></i>
              <span>Фильтры</span>
              <span
                v-if="activeFiltersCount > 0"
                class="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full"
              >
                {{ activeFiltersCount }}
              </span>
            </button>
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="resetFilters"
              class="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors duration-150"
            >
              <i class="pi pi-refresh mr-2"></i>
              Сбросить фильтры
            </button>
            <button
              @click="resetColumnWidths"
              class="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors duration-150"
            >
              <i class="pi pi-refresh mr-2"></i>
              Сбросить ширину колонок
            </button>
          </div>
        </div>
      </div>

      <div
        v-show="showFilters"
        class="px-6 py-4 border-b border-gray-200 bg-white"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Дата начала периода
            </label>
            <input
              v-model="filters.date_from"
              type="date"
              @change="
                () => {
                  filters.page = 1
                  updateURL()
                  loadPaymentPeriods()
                }
              "
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Дата конца периода
            </label>
            <input
              v-model="filters.date_to"
              type="date"
              @change="
                () => {
                  filters.page = 1
                  updateURL()
                  loadPaymentPeriods()
                }
              "
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Клиент
            </label>
            <input
              v-model="filters.payment_account_name"
              type="text"
              placeholder="Название клиента"
              @input="debouncedLoadPaymentPeriods"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              ID платежного аккаунта
            </label>
            <input
              v-model="filters.payment_account_id"
              type="number"
              placeholder="ID платежного аккаунта"
              @input="debouncedLoadPaymentPeriods"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Номер договора
            </label>
            <input
              v-model="filters.number_of_contract"
              type="text"
              placeholder="Номер договора"
              @input="debouncedLoadPaymentPeriods"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Юридическое лицо
            </label>
            <input
              v-model="filters.legal_name"
              type="text"
              placeholder="Название юр. лица"
              @input="debouncedLoadPaymentPeriods"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              ID договора
            </label>
            <input
              v-model="filters.contract_id"
              type="text"
              placeholder="ID договора"
              @input="debouncedLoadPaymentPeriods"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Тип оплаты
            </label>
            <select
              v-model="filters.is_prepayment"
              @change="
                () => {
                  filters.page = 1
                  updateURL()
                  loadPaymentPeriods()
                }
              "
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Все</option>
              <option value="false">Постоплата</option>
              <option value="true">Предоплата</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Период платежа
            </label>
            <input
              v-model="filters.payment_period"
              type="month"
              @change="
                () => {
                  filters.page = 1
                  updateURL()
                  loadPaymentPeriods()
                }
              "
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Статус
            </label>
            <select
              v-model="filters.draft"
              @change="
                () => {
                  filters.page = 1
                  updateURL()
                  loadPaymentPeriods()
                }
              "
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Все</option>
              <option value="true">Черновик</option>
              <option value="false">Опубликовано</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Оплачено
            </label>
            <select
              v-model="filters.paid"
              @change="
                () => {
                  filters.page = 1
                  updateURL()
                  loadPaymentPeriods()
                }
              "
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Все</option>
              <option value="true">Оплачено</option>
              <option value="false">Не оплачено</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Доставлено
            </label>
            <select
              v-model="filters.delivered"
              @change="
                () => {
                  filters.page = 1
                  updateURL()
                  loadPaymentPeriods()
                }
              "
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Все</option>
              <option value="true">Доставлено</option>
              <option value="false">Не доставлено</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Фейковый счет
            </label>
            <select
              v-model="filters.fake"
              @change="
                () => {
                  filters.page = 1
                  updateURL()
                  loadPaymentPeriods()
                }
              "
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Все</option>
              <option value="true">Фейк</option>
              <option value="false">Обычные</option>
            </select>
          </div>
        </div>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12">
        <div
          class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
        ></div>
        <span class="ml-3 text-gray-600">Загрузка данных...</span>
      </div>

      <div v-else-if="error" class="p-6 text-center">
        <div class="text-red-600 mb-2">
          <i class="pi pi-exclamation-triangle text-2xl"></i>
        </div>
        <p class="text-gray-700">{{ error }}</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table
          class="min-w-full divide-y divide-gray-200"
          style="min-width: 1280px"
        >
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-4 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50"
                :style="{ width: columnWidths[0] + 'px' }"
              >
                <div class="flex items-center justify-center">
                  <input
                    type="checkbox"
                    :checked="isAllSelected"
                    @change="toggleSelectAll"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <ColumnResizer
                  :column-index="0"
                  :min-width="40"
                  :max-width="60"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50"
                :style="{ width: columnWidths[1] + 'px' }"
              >
                <div class="flex items-center justify-center">
                  <span>Просмотр</span>
                </div>
                <ColumnResizer
                  :column-index="1"
                  :min-width="60"
                  :max-width="80"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[2] + 'px' }"
                @click="toggleSort('payment_account_name')"
              >
                <div class="flex items-center justify-between">
                  <span>Клиент</span>
                  <i
                    :class="getSortIcon('payment_account_name')"
                    class="ml-1"
                  ></i>
                </div>
                <ColumnResizer
                  :column-index="2"
                  :min-width="150"
                  :max-width="400"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[3] + 'px' }"
                @click="toggleSort('legal_name')"
              >
                <div class="flex items-center justify-between">
                  <span>Юр. лицо</span>
                  <i :class="getSortIcon('legal_name')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="3"
                  :min-width="100"
                  :max-width="300"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[4] + 'px' }"
                @click="toggleSort('payment_period')"
              >
                <div class="flex items-center justify-between">
                  <span>Период</span>
                  <i :class="getSortIcon('payment_period')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="4"
                  :min-width="80"
                  :max-width="200"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[5] + 'px' }"
                @click="toggleSort('delivered')"
              >
                <div class="flex items-center justify-center">
                  <span>Получено</span>
                  <i :class="getSortIcon('delivered')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="5"
                  :min-width="120"
                  :max-width="200"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[6] + 'px' }"
                @click="toggleSort('paid')"
              >
                <div class="flex items-center justify-center">
                  <span>Оплачено</span>
                  <i :class="getSortIcon('paid')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="6"
                  :min-width="80"
                  :max-width="150"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[7] + 'px' }"
                @click="toggleSort('sum_invoices')"
              >
                <div class="flex items-center justify-end">
                  <span>Сумма счетов</span>
                  <i :class="getSortIcon('sum_invoices')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="7"
                  :min-width="120"
                  :max-width="200"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[8] + 'px' }"
                @click="toggleSort('sum_acts')"
              >
                <div class="flex items-center justify-end">
                  <span>Сумма актов</span>
                  <i :class="getSortIcon('sum_acts')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="8"
                  :min-width="120"
                  :max-width="200"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[9] + 'px' }"
                @click="toggleSort('contract_id')"
              >
                <div class="flex items-center justify-between">
                  <span>ID договора</span>
                  <i :class="getSortIcon('contract_id')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="9"
                  :min-width="80"
                  :max-width="150"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[10] + 'px' }"
                @click="toggleSort('is_prepayment')"
              >
                <div class="flex items-center justify-center">
                  <span>Тип оплаты</span>
                  <i :class="getSortIcon('is_prepayment')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="10"
                  :min-width="120"
                  :max-width="200"
                  @resize="handleColumnResize"
                />
              </th>
              <th
                class="px-2 py-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 z-40 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative"
                :style="{ width: columnWidths[11] + 'px' }"
                @click="toggleSort('draft')"
              >
                <div class="flex items-center justify-center">
                  <span>Статус</span>
                  <i :class="getSortIcon('draft')" class="ml-1"></i>
                </div>
                <ColumnResizer
                  :column-index="11"
                  :min-width="100"
                  :max-width="150"
                  @resize="handleColumnResize"
                />
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(item, index) in paginatedData"
              :key="`data-row-${index}`"
              class="hover:bg-gray-50 transition-colors duration-150"
            >
              <td
                class="px-4 py-1 text-xs text-gray-900 text-center"
                :style="{ width: columnWidths[0] + 'px' }"
              >
                <div class="flex justify-center">
                  <input
                    type="checkbox"
                    :checked="selectedItems.has(index)"
                    @change="toggleSelectItem(index)"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900 text-center"
                :style="{ width: columnWidths[1] + 'px' }"
              >
                <button
                  v-if="
                    item.contract_id &&
                    item.payment_period &&
                    item.payment_account_id
                  "
                  @click="
                    viewPaymentPeriodDetail(
                      item.payment_account_id,
                      item.payment_period,
                      item.contract_id
                    )
                  "
                  class="text-blue-600 hover:text-blue-800 transition-colors duration-150 block mx-auto"
                  title="Просмотреть детали платежного периода"
                >
                  <i class="pi pi-eye text-lg"></i>
                </button>
                <span v-else class="text-gray-400">
                  <!-- Отладочная информация -->
                  <div class="text-xs text-gray-500">
                    <div>contract_id: {{ item.contract_id || 'null' }}</div>
                    <div>
                      payment_period: {{ item.payment_period || 'null' }}
                    </div>
                    <div>
                      payment_account_id:
                      {{ item.payment_account_id || 'null' }}
                    </div>
                  </div>
                </span>
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900 truncate"
                :style="{ width: columnWidths[2] + 'px' }"
                :title="item.payment_account_name || ''"
              >
                <router-link
                  v-if="item.payment_account_id"
                  :to="`/payment-accounts/${item.payment_account_id}`"
                  class="text-blue-600 hover:text-blue-800 font-medium block truncate"
                >
                  {{ item.payment_account_name || '' }}
                </router-link>
                <span v-else class="block truncate">{{
                  item.payment_account_name || ''
                }}</span>
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900 truncate"
                :style="{ width: columnWidths[3] + 'px' }"
                :title="item.legal_name || ''"
              >
                {{ item.legal_name || '' }}
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900 truncate"
                :style="{ width: columnWidths[4] + 'px' }"
                :title="
                  item.payment_period ? formatDate(item.payment_period) : ''
                "
              >
                {{ item.payment_period ? formatDate(item.payment_period) : '' }}
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900"
                :style="{ width: columnWidths[5] + 'px' }"
              >
                <span
                  :class="{
                    'px-2 py-1 text-xs text-center font-medium rounded-full truncate block': true,
                    'bg-green-100 text-green-800': item.delivered,
                    'bg-red-100 text-red-800': !item.delivered
                  }"
                  :title="item.delivered ? 'Да' : 'Нет'"
                >
                  {{ item.delivered ? 'Да' : 'Нет' }}
                </span>
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900"
                :style="{ width: columnWidths[6] + 'px' }"
              >
                <span
                  :class="{
                    'px-2 py-1 text-xs text-center font-medium rounded-full truncate block': true,
                    'bg-green-100 text-green-800': item.paid,
                    'bg-red-100 text-red-800': !item.paid
                  }"
                  :title="item.paid ? 'Да' : 'Нет'"
                >
                  {{ item.paid ? 'Да' : 'Нет' }}
                </span>
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900 text-right truncate"
                :style="{ width: columnWidths[7] + 'px' }"
                :title="formatCurrency(item.sum_invoices)"
              >
                {{ formatCurrency(item.sum_invoices) }}
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900 text-right truncate"
                :style="{ width: columnWidths[8] + 'px' }"
                :title="formatCurrency(item.sum_acts)"
              >
                {{ formatCurrency(item.sum_acts) }}
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900 truncate"
                :style="{ width: columnWidths[9] + 'px' }"
                :title="item.contract_id || ''"
              >
                <button
                  v-if="item.contract_id"
                  @click="viewContract(item.contract_id)"
                  class="text-blue-600 hover:text-blue-900 hover:underline transition-colors duration-150 font-medium block truncate"
                  title="Перейти к договору"
                >
                  {{ item.contract_id }}
                </button>
                <span v-else class="text-gray-400">-</span>
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900"
                :style="{ width: columnWidths[10] + 'px' }"
              >
                <span
                  :class="{
                    'px-2 py-1 text-xs text-center font-medium rounded-full truncate block': true,
                    'bg-blue-100 text-blue-800': item.is_prepayment,
                    'bg-green-100 text-green-800': !item.is_prepayment
                  }"
                  :title="item.is_prepayment ? 'Предоплата' : 'Постоплата'"
                >
                  {{ item.is_prepayment ? 'Предоплата' : 'Постоплата' }}
                </span>
              </td>
              <td
                class="px-2 py-1 text-xs text-gray-900"
                :style="{ width: columnWidths[11] + 'px' }"
              >
                <span
                  :class="{
                    'px-2 py-1 text-xs text-center font-medium rounded-full truncate block': true,
                    'bg-orange-100 text-orange-800': item.draft,
                    'bg-green-100 text-green-800': !item.draft
                  }"
                  :title="item.draft ? 'Черновик' : 'Опубликовано'"
                >
                  {{ item.draft ? 'Черновик' : 'Опубликовано' }}
                </span>
              </td>
            </tr>

            <tr
              v-for="totalItem in total"
              v-if="totalItem"
              :key="`total-${totalItem.sum_invoices || 0}-${totalItem.sum_acts || 0}`"
              class="bg-gray-50 hover:bg-gray-100 transition-colors duration-150 border-t-2 border-gray-300"
            >
              <td
                colspan="9"
                class="px-4 py-3 text-sm text-gray-900 font-semibold text-center"
              >
                Итого по всем договорам
              </td>

              <td
                class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold"
                :style="{ width: columnWidths[8] + 'px' }"
              >
                {{
                  totalItem.sum_invoices
                    ? formatCurrency(totalItem.sum_invoices)
                    : '0'
                }}
              </td>
              <td
                class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold"
                :style="{ width: columnWidths[9] + 'px' }"
              >
                {{
                  totalItem.sum_acts ? formatCurrency(totalItem.sum_acts) : '0'
                }}
              </td>
            </tr>
          </tbody>

          <tfoot v-if="total">
            <tr class="bg-gray-100">
              <td
                colspan="8"
                class="px-4 py-3 text-sm font-medium text-gray-700 text-left"
              >
                Итого:
              </td>
              <td
                class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold bg-gray-100"
              >
                {{ formatCurrency(total.sum_invoices || 0) }}
              </td>
              <td
                class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold bg-gray-100"
              >
                {{ formatCurrency(total.sum_acts || 0) }}
              </td>
              <td
                colspan="3"
                class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold bg-gray-100"
              ></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Пагинация -->
      <div
        v-if="data && data.length > 0"
        class="bg-gray-50 px-6 py-3 border-t border-gray-200"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-500">Показать:</span>
              <select
                :value="filters.limit"
                @change="handleLimitChange($event.target.value)"
                class="px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
              <span class="text-sm text-gray-500">записей</span>
            </div>
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="exportToCSV"
              :disabled="exportLoading"
              class="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 hover:border-green-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i v-if="!exportLoading" class="pi pi-download mr-2"></i>
              <i v-else class="pi pi-spin pi-spinner mr-2"></i>
              {{ exportLoading ? 'Выгрузка...' : 'Выгрузить в CSV' }}
            </button>
            <button
              @click="handlePageChange(filters.page - 1)"
              :disabled="filters.page <= 1"
              class="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Назад
            </button>
            <span class="text-sm text-gray-500">
              Страница {{ filters.page }} из {{ totalPages }}
            </span>
            <button
              @click="handlePageChange(filters.page + 1)"
              :disabled="filters.page >= totalPages"
              class="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Вперед
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch, nextTick } from 'vue'
  import { useRouter } from 'vue-router'
  import { paymentsApi, billingApi, invoiceApi } from '../services/api'
  import ColumnResizer from '../components/ColumnResizer.vue'
  import InvoiceForm from '../components/InvoiceForm.vue'
  import ActForm from '../components/ActForm.vue'
  import { useToast } from 'primevue/usetoast'
  import { getApiErrorMessage } from '../utils/apiError'

  const toast = useToast()
  const router = useRouter()

  const loading = ref(false)
  const exportLoading = ref(false)
  const error = ref('')
  const data = ref([])
  const total = ref([])
  const totalCount = ref(0)
  const selectedItems = ref(new Set())
  const copyLoading = ref(false)

  const showAddActModal = ref(false)

  const actForm = ref({
    contract_id: '',
    date_of_act: '',
    payment_period: '',
    number_of_act: '',
    amount: '',
    comment: ''
  })

  const showAddInvoiceModal = ref(false)
  const invoiceForm = ref({})

  const showFilters = ref(false)
  const isResizing = ref(false)
  const lastResizeTime = ref(0)

  const defaultColumnWidths = {
    0: 50, // Чекбокс
    1: 60, // Просмотр
    2: 200, // Клиент
    3: 100, // Юр. лицо
    4: 120, // Период
    5: 120, // Получено
    6: 100, // Оплачено
    7: 120, // Сумма счетов
    8: 120, // Сумма актов
    9: 80, // ID договора
    10: 120, // Тип оплаты
    11: 100 // Статус
  }

  const columnWidths = ref({ ...defaultColumnWidths })

  const getFirstDayOfPreviousMonth = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = today.getMonth()

    // Если текущий месяц январь (0), то предыдущий месяц - декабрь прошлого года
    if (month === 0) {
      return `${year - 1}-12-01`
    } else {
      return `${year}-${String(month).padStart(2, '0')}-01`
    }
  }

  const getTodayString = () => {
    const today = new Date()
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  }

  const filters = ref({
    date_from: getFirstDayOfPreviousMonth(),
    date_to: getTodayString(),
    payment_account_id: '',
    payment_account_name: '',
    number_of_contract: '',
    contract_id: '',
    legal_name: '',
    is_prepayment: '',
    payment_period: '',
    draft: '',
    paid: '',
    delivered: '',
    fake: 'false',
    sort: 'payment_period',
    order: 'desc',
    page: 1,
    limit: 50
  })

  const isAllSelected = computed(() => {
    return (
      data.value.length > 0 && selectedItems.value.size === data.value.length
    )
  })

  const activeFiltersCount = computed(() => {
    let count = 0
    if (filters.value.date_from) count++
    if (filters.value.date_to) count++
    if (filters.value.payment_account_id) count++
    if (filters.value.payment_account_name) count++
    if (filters.value.number_of_contract) count++
    if (filters.value.contract_id) count++
    if (filters.value.legal_name) count++
    if (filters.value.is_prepayment !== '') count++
    if (filters.value.payment_period) count++
    if (filters.value.draft !== '') count++
    if (filters.value.paid !== '') count++
    if (filters.value.delivered !== '') count++
    if (filters.value.fake === 'true' || filters.value.fake === '') count++
    return count
  })

  const totalPages = computed(() => {
    if (!totalCount.value || !filters.value.limit) return 1
    return Math.ceil(totalCount.value / filters.value.limit)
  })

  const paginatedData = computed(() => {
    return data.value
  })

  const loadPaymentPeriods = async () => {
    if (!filters.value.date_from || !filters.value.date_to) {
      error.value = 'Пожалуйста, укажите даты начала и конца периода'
      return
    }

    loading.value = true
    error.value = ''

    try {
      const params = {
        date_from: filters.value.date_from,
        date_to: filters.value.date_to,
        page: filters.value.page,
        limit: filters.value.limit,
        sort: filters.value.sort || 'payment_period',
        order: filters.value.order || 'desc'
      }

      const filtersObj = {}
      if (filters.value.payment_account_id) {
        filtersObj.payment_account_id = filters.value.payment_account_id
      }
      if (filters.value.payment_account_name) {
        filtersObj.payment_account_name = filters.value.payment_account_name
      }
      if (filters.value.number_of_contract) {
        filtersObj.number_of_contract = filters.value.number_of_contract
      }
      if (filters.value.contract_id) {
        filtersObj.contract_id = filters.value.contract_id
      }
      if (filters.value.legal_name) {
        filtersObj.legal_name = filters.value.legal_name
      }
      if (filters.value.is_prepayment !== '') {
        filtersObj.is_prepayment = filters.value.is_prepayment === 'true'
      }
      if (filters.value.payment_period) {
        const [year, month] = filters.value.payment_period.split('-')
        filtersObj.payment_period = `${year}-${month}-01`
      }
      if (filters.value.draft !== '') {
        filtersObj.draft = filters.value.draft === 'true'
      }
      if (filters.value.paid !== '') {
        filtersObj.paid = filters.value.paid === 'true'
      }
      if (filters.value.delivered !== '') {
        filtersObj.delivered = filters.value.delivered === 'true'
      }
      if (filters.value.fake !== '') {
        filtersObj.fake = filters.value.fake === 'true'
      }

      if (Object.keys(filtersObj).length > 0) {
        params.filters = filtersObj
      }

      const response = await paymentsApi.getPaymentPeriodReport(params)

      if (response && response.data) {
        data.value = response.data
        total.value = response.total || []
        // Используем общее количество записей из API, а не количество на текущей странице
        totalCount.value = response.total_count || response.data?.length || 0
        // Очищаем выбранные элементы при загрузке новых данных
        selectedItems.value.clear()
      } else {
        data.value = []
        total.value = []
        totalCount.value = 0
        selectedItems.value.clear()
      }
    } catch (err) {
      error.value = err.message || 'Ошибка при загрузке данных'
      data.value = []
      total.value = []
      totalCount.value = 0
      selectedItems.value.clear()
    } finally {
      loading.value = false
    }
  }

  const exportToCSV = async () => {
    exportLoading.value = true

    try {
      const params = {
        date_from: filters.value.date_from,
        date_to: filters.value.date_to,
        page: 1,
        limit: 10000,
        sort: filters.value.sort,
        order: filters.value.order
      }

      const filterFields = [
        'payment_account_id',
        'payment_account_name',
        'number_of_contract',
        'contract_id',
        'legal_name',
        'payment_period',
        'draft',
        'paid',
        'delivered'
      ]

      const filtersObj = {}
      filterFields.forEach(field => {
        const value = filters.value[field]
        if (value !== '' && value !== null && value !== undefined) {
          filtersObj[field] = value
        }
      })

      if (
        filters.value.is_prepayment !== '' &&
        filters.value.is_prepayment !== null &&
        filters.value.is_prepayment !== undefined
      ) {
        filtersObj.is_prepayment = filters.value.is_prepayment === 'true'
      }
      if (filters.value.fake !== '') {
        filtersObj.fake = filters.value.fake === 'true'
      }

      if (Object.keys(filtersObj).length > 0) {
        params.filters = filtersObj
      }

      const response = await paymentsApi.downloadPaymentPeriodReport(params)

      if (response) {
        const blob = response

        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `payment_periods_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(link.href)
      }

      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Файл CSV успешно выгружен',
        life: 3000
      })
    } catch (error) {
      console.error('Ошибка при экспорте CSV:', error)
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось выгрузить файл CSV',
        life: 3000
      })
    } finally {
      exportLoading.value = false
    }
  }

  const handleColumnResize = ({ columnIndex, width }) => {
    isResizing.value = true
    lastResizeTime.value = Date.now()
    columnWidths.value[columnIndex] = width

    nextTick(() => {
      const table = document.querySelector('table')
      if (table) {
        const headerCells = table.querySelectorAll('thead th')
        const dataCells = table.querySelectorAll(
          `tbody tr td:nth-child(${columnIndex + 1})`
        )

        if (headerCells[columnIndex]) {
          headerCells[columnIndex].style.width = `${width}px`
          headerCells[columnIndex].style.minWidth = `${width}px`
          headerCells[columnIndex].style.maxWidth = `${width}px`
        }

        dataCells.forEach(cell => {
          cell.style.width = `${width}px`
          cell.style.minWidth = `${width}px`
          cell.style.maxWidth = `${width}px`
        })
      }

      setTimeout(() => {
        isResizing.value = false
      }, 300)
    })
  }

  const resetColumnWidths = () => {
    isResizing.value = true
    lastResizeTime.value = Date.now()
    columnWidths.value = { ...defaultColumnWidths }

    nextTick(() => {
      const table = document.querySelector('table')
      if (table) {
        const headerCells = table.querySelectorAll('thead th')
        const dataRows = table.querySelectorAll('tbody tr')

        Object.entries(columnWidths.value).forEach(([index, width]) => {
          const colIndex = parseInt(index)
          if (headerCells[colIndex]) {
            headerCells[colIndex].style.width = `${width}px`
            headerCells[colIndex].style.minWidth = `${width}px`
            headerCells[colIndex].style.maxWidth = `${width}px`
          }

          dataRows.forEach(row => {
            const cell = row.querySelector(`td:nth-child(${colIndex + 1})`)
            if (cell) {
              cell.style.width = `${width}px`
              cell.style.minWidth = `${width}px`
              cell.style.maxWidth = `${width}px`
            }
          })
        })
      }

      setTimeout(() => {
        isResizing.value = false
      }, 300)
    })
  }

  const addAct = () => {
    showAddActModal.value = true
    resetActForm()
  }

  const viewContract = contractId => {
    if (contractId) {
      router.push(`/contracts/${contractId}`)
    }
  }

  const viewPaymentPeriodDetail = (
    paymentAccountId,
    paymentPeriod,
    contractId
  ) => {
    console.log('🔗 viewPaymentPeriodDetail called with:', {
      paymentAccountId,
      paymentPeriod,
      contractId
    })

    if (paymentAccountId && paymentPeriod && contractId) {
      const query = {}
      if (filters.value.fake === 'true') query.fake = 'true'
      else if (filters.value.fake === 'false') query.fake = 'false'

      router.push({
        path: `/payment-periods/${paymentAccountId}/${paymentPeriod}/`,
        query
      })
    } else {
      console.warn('⚠️ Missing required parameters:', {
        paymentAccountId,
        paymentPeriod,
        contractId
      })
    }
  }

  const getSortIcon = field => {
    if (filters.value.sort !== field) {
      return 'pi pi-sort-alt text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200'
    }
    return filters.value.order === 'asc'
      ? 'pi pi-sort-up text-blue-600'
      : 'pi pi-sort-down text-blue-600'
  }

  const resetActForm = () => {
    actForm.value = {
      contract_id: '',
      date_of_act: '',
      payment_period: '',
      number_of_act: '',
      amount: '',
      comment: ''
    }
  }

  const handleActSubmit = async formData => {
    try {
      await billingApi.post('/act', formData)
      showAddActModal.value = false
      resetActForm()
      loadPaymentPeriods()
      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Акт успешно создан!',
        life: 3000
      })
    } catch (error) {
      console.error('Ошибка при создании акта:', error)
      const errorData = error.response?.data
      let errorMessage = 'Ошибка при создании акта'

      const details = errorData?.data?.details || errorData?.details

      if (details) {
        const firstField = Object.keys(details)[0]
        if (firstField) {
          const fieldError = details[firstField]
          if (Array.isArray(fieldError) && fieldError.length > 0) {
            errorMessage = `${firstField}: ${fieldError[0]}`
          } else if (typeof fieldError === 'string') {
            errorMessage = `${firstField}: ${fieldError}`
          }
        }
      } else if (errorData?.data?.message) {
        errorMessage = errorData.data.message
      } else if (errorData?.message) {
        errorMessage = errorData.message
      } else if (error.message) {
        errorMessage = error.message
      }

      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: errorMessage,
        life: 5000
      })
    }
  }

  const addInvoice = () => {
    showAddInvoiceModal.value = true
    invoiceForm.value = {}
  }

  const handleInvoiceSubmit = async formData => {
    try {
      await invoiceApi.create(formData)
      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Счет создан',
        life: 3000
      })
      showAddInvoiceModal.value = false
      loadPaymentPeriods()
    } catch (err) {
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: getApiErrorMessage(err, 'Ошибка при создании счета'),
        life: 5000
      })
    }
  }

  const resetFilters = () => {
    filters.value = {
      date_from: getFirstDayOfPreviousMonth(),
      date_to: getTodayString(),
      payment_account_id: '',
      payment_account_name: '',
      number_of_contract: '',
      contract_id: '',
      legal_name: '',
      is_prepayment: '',
      payment_period: '',
      draft: '',
      paid: '',
      delivered: '',
      fake: 'false',
      sort: 'payment_period',
      order: 'desc',
      page: 1,
      limit: 50
    }
    updateURL()
    loadPaymentPeriods()
  }

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages.value) {
      filters.value.page = newPage
      updateURL()
      loadPaymentPeriods()
    }
  }

  const handleLimitChange = newLimit => {
    const parsedLimit = parseInt(newLimit)
    if (!isNaN(parsedLimit) && parsedLimit > 0) {
      filters.value.limit = parsedLimit
      filters.value.page = 1
      updateURL()
      loadPaymentPeriods()
    }
  }

  const updateURL = () => {
    const query = {}

    // Добавляем только непустые значения
    if (filters.value.date_from) query.date_from = filters.value.date_from
    if (filters.value.date_to) query.date_to = filters.value.date_to
    if (filters.value.payment_account_id)
      query.payment_account_id = filters.value.payment_account_id
    if (filters.value.payment_account_name)
      query.payment_account_name = filters.value.payment_account_name
    if (filters.value.number_of_contract)
      query.number_of_contract = filters.value.number_of_contract
    if (filters.value.contract_id) query.contract_id = filters.value.contract_id
    if (filters.value.legal_name) query.legal_name = filters.value.legal_name
    if (filters.value.is_prepayment !== '')
      query.is_prepayment = filters.value.is_prepayment
    if (filters.value.payment_period)
      query.payment_period = filters.value.payment_period
    if (filters.value.draft !== '') query.draft = filters.value.draft
    if (filters.value.paid !== '') query.paid = filters.value.paid
    if (filters.value.delivered !== '')
      query.delivered = filters.value.delivered
    if (filters.value.fake === 'true') query.fake = 'true'
    else if (filters.value.fake === '') query.fake = 'all'
    if (filters.value.sort) query.sort = filters.value.sort
    if (filters.value.order) query.order = filters.value.order
    if (filters.value.page > 1) query.page = filters.value.page
    if (filters.value.limit !== 50) {
      query.limit = filters.value.limit
    }

    const url = new URL(window.location)
    url.search = new URLSearchParams(query).toString()
    window.history.pushState({}, '', url)
  }

  const loadFromURL = () => {
    const url = new URL(window.location)
    const params = url.searchParams

    if (params.get('date_from'))
      filters.value.date_from = params.get('date_from')
    else filters.value.date_from = getFirstDayOfPreviousMonth()

    if (params.get('date_to')) filters.value.date_to = params.get('date_to')
    else filters.value.date_to = getTodayString()
    if (params.get('payment_account_id'))
      filters.value.payment_account_id = params.get('payment_account_id')
    if (params.get('payment_account_name'))
      filters.value.payment_account_name = params.get('payment_account_name')
    if (params.get('number_of_contract'))
      filters.value.number_of_contract = params.get('number_of_contract')
    if (params.get('contract_id'))
      filters.value.contract_id = params.get('contract_id')
    if (params.get('legal_name'))
      filters.value.legal_name = params.get('legal_name')
    if (params.get('is_prepayment'))
      filters.value.is_prepayment = params.get('is_prepayment')
    if (params.get('payment_period'))
      filters.value.payment_period = params.get('payment_period')
    if (params.get('draft')) filters.value.draft = params.get('draft')
    if (params.get('paid')) filters.value.paid = params.get('paid')
    if (params.get('delivered'))
      filters.value.delivered = params.get('delivered')
    const fakeParam = params.get('fake')
    if (fakeParam === 'true') filters.value.fake = 'true'
    else if (fakeParam === 'all') filters.value.fake = ''
    else filters.value.fake = 'false'
    if (params.get('sort')) filters.value.sort = params.get('sort')
    if (params.get('order')) filters.value.order = params.get('order')
    if (params.get('page')) filters.value.page = parseInt(params.get('page'))
    if (params.get('limit')) {
      const parsedLimit = parseInt(params.get('limit'))
      if (!isNaN(parsedLimit) && parsedLimit > 0) {
        filters.value.limit = parsedLimit
      }
    }
  }

  const formatDate = dateString => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const month = date.toLocaleDateString('ru-RU', { month: 'long' })
      const year = date.getFullYear()
      return `${month} ${year}`
    } catch (error) {
      return dateString
    }
  }

  const formatCurrency = amount => {
    if (!amount && amount !== 0) return '0'
    try {
      return new Intl.NumberFormat('ru-RU', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    } catch (error) {
      return `${amount}`
    }
  }

  let debounceTimer = null
  const debouncedLoadPaymentPeriods = () => {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      filters.value.page = 1 // Сбрасываем страницу при изменении фильтров
      updateURL()
      loadPaymentPeriods()
    }, 500)
  }

  const toggleSort = field => {
    if (isResizing.value || Date.now() - lastResizeTime.value < 500) {
      return
    }

    if (filters.value.sort === field) {
      filters.value.order = filters.value.order === 'asc' ? 'desc' : 'asc'
    } else {
      filters.value.sort = field
      filters.value.order = 'asc'
    }
    filters.value.page = 1 // Сбрасываем страницу при изменении сортировки
    updateURL()
    loadPaymentPeriods()
  }

  const toggleSelectAll = () => {
    if (isAllSelected.value) {
      selectedItems.value.clear()
    } else {
      selectedItems.value.clear()
      data.value.forEach((_, index) => {
        selectedItems.value.add(index)
      })
    }
  }

  const toggleSelectItem = index => {
    if (selectedItems.value.has(index)) {
      selectedItems.value.delete(index)
    } else {
      selectedItems.value.add(index)
    }
  }

  const copyInvoices = async () => {
    if (selectedItems.value.size === 0) {
      toast.add({
        severity: 'warn',
        summary: 'Предупреждение',
        detail: 'Выберите хотя бы один элемент для копирования',
        life: 3000
      })
      return
    }

    copyLoading.value = true

    try {
      const selectedData = Array.from(selectedItems.value).map(index => {
        const item = data.value[index]
        return {
          payment_account_id: item.payment_account_id,
          payment_period: item.payment_period
        }
      })

      await billingApi.post('/invoice/copy', selectedData)

      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: `Скопировано ${selectedData.length} счетов`,
        life: 3000
      })

      selectedItems.value.clear()
    } catch (error) {
      console.error('Ошибка при копировании счетов:', error)
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail:
          'Не удалось скопировать счета: ' +
          (error.response?.data?.message || error.message),
        life: 5000
      })
    } finally {
      copyLoading.value = false
    }
  }

  onMounted(() => {
    loadFromURL() // Загружаем параметры из URL
    loadPaymentPeriods()
  })
</script>

<style scoped>
  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .truncate:hover {
    overflow: visible;
    white-space: normal;
    word-break: break-word;
    position: relative;
    z-index: 10;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    padding: 4px 8px;
    margin: -4px -8px;
  }
</style>
